import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { formatDate, formatDateTime, apiErrorMsg } from '@/lib/formatters';
import { Plus, Users, Shield, ClipboardList, Settings as SettingsIcon, Edit, Eye } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const ROLE_LABELS = { director: 'Director', manager: 'Sales Manager', executive: 'Sales Executive', finance: 'Finance' };
const ROLE_COLORS = {
  director: 'bg-purple-100 text-purple-800',
  manager: 'bg-blue-100 text-blue-800',
  executive: 'bg-emerald-100 text-emerald-800',
  finance: 'bg-amber-100 text-amber-800',
};

export default function SettingsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('users');

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Settings & Administration</h1>
        <p className="text-slate-500 text-sm mt-0.5">Manage users, roles, and system configuration</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="users" data-testid="settings-users-tab"><Users size={14} className="mr-1" /> Users</TabsTrigger>
          <TabsTrigger value="audit" data-testid="settings-audit-tab"><ClipboardList size={14} className="mr-1" /> Audit Logs</TabsTrigger>
          <TabsTrigger value="reports" data-testid="settings-reports-tab"><SettingsIcon size={14} className="mr-1" /> MIS Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="users"><UsersTab /></TabsContent>
        <TabsContent value="audit"><AuditTab /></TabsContent>
        <TabsContent value="reports"><ReportsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const fetchUsers = () => {
    api.get('/users').then(r => { setUsers(r.data.users); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { fetchUsers(); }, []);

  const toggleActive = async (userId, isActive) => {
    try {
      await api.put(`/users/${userId}`, { is_active: !isActive });
      toast.success('User updated');
      fetchUsers();
    } catch (err) { toast.error(apiErrorMsg(err)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{users.length} users</p>
        <Button onClick={() => setShowCreate(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-user-btn">
          <Plus size={16} /> Add User
        </Button>
      </div>

      <div className="table-container bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Name</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Email</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Role</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Phone</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Active</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>{Array.from({ length: 6 }).map((_, j) => <TableCell key={j}><div className="h-4 bg-slate-100 rounded animate-pulse" /></TableCell>)}</TableRow>
              ))
            ) : (
              users.map(u => (
                <TableRow key={u._id} data-testid={`user-row-${u._id}`}>
                  <TableCell className="font-semibold text-sm">{u.name}</TableCell>
                  <TableCell className="text-sm text-slate-600">{u.email}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[u.role] || 'bg-slate-100'}`}>
                      {ROLE_LABELS[u.role] || u.role}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{u.phone || '-'}</TableCell>
                  <TableCell>
                    {u.is_demo && <Badge variant="outline" className="text-[10px]">Demo</Badge>}
                  </TableCell>
                  <TableCell>
                    <Switch checked={u.is_active !== false} onCheckedChange={() => toggleActive(u._id, u.is_active !== false)} data-testid={`toggle-user-${u._id}`} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {showCreate && <CreateUserDialog open onClose={() => setShowCreate(false)} onCreated={() => { fetchUsers(); setShowCreate(false); }} />}
    </div>
  );
}

function CreateUserDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'executive', phone: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/users', form);
      toast.success('User created');
      onCreated();
    } catch (err) { toast.error(apiErrorMsg(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Add New User</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div><Label>Full Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required data-testid="user-name-input" /></div>
          <div><Label>Email *</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required data-testid="user-email-input" /></div>
          <div><Label>Password *</Label><Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required data-testid="user-password-input" /></div>
          <div>
            <Label>Role</Label>
            <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="director">Director</SelectItem>
                <SelectItem value="manager">Sales Manager</SelectItem>
                <SelectItem value="executive">Sales Executive</SelectItem>
                <SelectItem value="finance">Finance</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-user-submit">{saving ? 'Creating...' : 'Create User'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AuditTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    api.get('/audit-logs', { params: { page, limit: 30 } })
      .then(r => { setLogs(r.data.logs); setTotal(r.data.total); setLoading(false); })
      .catch(() => setLoading(false));
  }, [page]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">{total} audit entries</p>
      <div className="table-container bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Timestamp</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">User</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Role</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Action</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Entity</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>{Array.from({ length: 6 }).map((_, j) => <TableCell key={j}><div className="h-4 bg-slate-100 rounded animate-pulse" /></TableCell>)}</TableRow>
              ))
            ) : logs.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No audit logs</TableCell></TableRow>
            ) : (
              logs.map(log => (
                <TableRow key={log._id}>
                  <TableCell className="text-xs text-slate-500">{formatDateTime(log.created_at)}</TableCell>
                  <TableCell className="text-sm font-medium">{log.user_name}</TableCell>
                  <TableCell className="text-xs">{log.user_role}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{log.action}</Badge></TableCell>
                  <TableCell className="text-xs text-slate-500">{log.entity_type}</TableCell>
                  <TableCell className="text-xs text-slate-500 max-w-xs truncate">{log.details}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function ReportsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/reports/mis').then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-8 text-slate-400">Loading reports...</div>;
  if (!data) return <div className="text-center py-8 text-slate-400">No report data available</div>;

  return (
    <div className="space-y-6">
      {/* Executive Performance */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Executive Performance</CardTitle>
        </CardHeader>
        <CardContent>
          {data.executive_performance?.length > 0 ? (
            <>
              <div className="h-64 mb-4">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.executive_performance} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748B' }} />
                    <YAxis tick={{ fontSize: 12, fill: '#64748B' }} />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0' }} />
                    <Bar dataKey="leads_assigned" fill="#3B82F6" name="Leads" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="bookings" fill="#10B981" name="Bookings" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="visits_completed" fill="#F59E0B" name="Visits" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="table-container">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs uppercase font-semibold">Executive</TableHead>
                      <TableHead className="text-xs uppercase font-semibold">Leads</TableHead>
                      <TableHead className="text-xs uppercase font-semibold">Bookings</TableHead>
                      <TableHead className="text-xs uppercase font-semibold">Visits</TableHead>
                      <TableHead className="text-xs uppercase font-semibold">Conversion</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.executive_performance.map(ep => (
                      <TableRow key={ep.email}>
                        <TableCell className="font-medium text-sm">{ep.name}</TableCell>
                        <TableCell className="text-sm">{ep.leads_assigned}</TableCell>
                        <TableCell className="text-sm font-semibold text-emerald-600">{ep.bookings}</TableCell>
                        <TableCell className="text-sm">{ep.visits_completed}</TableCell>
                        <TableCell className="text-sm font-semibold">{ep.conversion_rate}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <p className="text-sm text-slate-400 text-center py-4">No executive data</p>
          )}
        </CardContent>
      </Card>

      {/* Project Sales */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Project-wise Sales</CardTitle>
        </CardHeader>
        <CardContent>
          {data.project_sales?.length > 0 ? (
            <div className="table-container">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs uppercase font-semibold">Project</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Total Units</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Sold/Booked</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Available</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Leads</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.project_sales.map(ps => (
                    <TableRow key={ps.name}>
                      <TableCell className="font-medium text-sm">{ps.name}</TableCell>
                      <TableCell className="text-sm">{ps.total_units}</TableCell>
                      <TableCell className="text-sm font-semibold text-emerald-600">{ps.sold}</TableCell>
                      <TableCell className="text-sm text-blue-600">{ps.available}</TableCell>
                      <TableCell className="text-sm">{ps.leads}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-4">No project data</p>
          )}
        </CardContent>
      </Card>

      {/* Source Analysis */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Lead Source Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          {data.source_analysis?.length > 0 ? (
            <div className="table-container">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs uppercase font-semibold">Source</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Total Leads</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Booked</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Conversion</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.source_analysis.map(sa => (
                    <TableRow key={sa.source}>
                      <TableCell className="font-medium text-sm">{sa.source?.replace(/_/g, ' ')}</TableCell>
                      <TableCell className="text-sm">{sa.total}</TableCell>
                      <TableCell className="text-sm font-semibold text-emerald-600">{sa.booked}</TableCell>
                      <TableCell className="text-sm">{sa.conversion}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-4">No source data</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
